// src/components/Layout.js
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../utils/permissions';
import { alertsService } from '../services/api';
import { 
  LayoutDashboard, Zap, Users, Receipt, DollarSign, Bell, 
  Settings, LogOut, Menu, X, User, FileText, Eye, Shield, BookOpen, Smartphone, Sun, Droplets
} from 'lucide-react';


const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { hasPermission } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadAlertsCount, setUnreadAlertsCount] = useState(0);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const isAdmin = user?.role === "administrateur";

  // ✅ Détecter si on est sur mobile
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      
      // Sur desktop, forcer sidebar ouverte
      if (!mobile) {
        setMobileMenuOpen(false);
        setSidebarOpen(true);
      } else {
        // Sur mobile, fermer la sidebar par défaut
        setSidebarOpen(false);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ✅ Fermer le menu mobile lors du changement de page
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // ✅ NOUVEAU : sur mobile, quand le menu est ouvert, on affiche toujours icône + texte
  const showLabels = isMobile ? mobileMenuOpen : sidebarOpen;

  // Configuration des items du menu avec leurs permissions
  const menuItems = [
    { path: '/', icon: LayoutDashboard, label: 'Tableau de bord', permission: PERMISSIONS.VIEW_DASHBOARD },
    { path: '/compteurs', icon: Zap, label: 'Compteurs', permission: PERMISSIONS.VIEW_METERS },
    { path: '/clients', icon: Users, label: 'Clients', permission: PERMISSIONS.VIEW_CUSTOMERS },
    { path: '/transactions', icon: Receipt, label: 'Transactions', permission: PERMISSIONS.VIEW_TRANSACTIONS },
    { path: '/redevances', icon: DollarSign, label: 'Redevances', permission: PERMISSIONS.VIEW_FEES },
    { path: '/alertes', icon: Bell, label: 'Alertes', permission: PERMISSIONS.VIEW_ALERTS },
    { path: '/rapports', icon: FileText, label: 'Rapports', permission: PERMISSIONS.VIEW_REPORTS },
    { path: '/centre_avance', icon: Settings, label: 'Centre Avancé', permission: PERMISSIONS.CONFIGURE_METER },
    { path: '/surveillance', icon: Eye, label: 'Surveillances Clients', permission: PERMISSIONS.VIEW_MONITORING },
    { path: '/utilisateurs', icon: User, label: 'Utilisateurs', permission: PERMISSIONS.VIEW_USERS },
    { path: '/comptabilite', icon: BookOpen, label: 'Comptabilité', permission: PERMISSIONS.VIEW_FEES },
    { path: '/contrats', icon: FileText, label: 'Contrats', permission: PERMISSIONS.VIEW_CONTRACTS },
    { path: '/remote-control', icon: Smartphone, label: 'Contrôle à Distance', permission: PERMISSIONS.VIEW_REMOTE_CONTROL },
    { path: '/gomelong-remote', icon: Droplets, label: 'Contrôle GOMELONG', permission: PERMISSIONS.VIEW_REMOTE_CONTROL }, // ✅ AJOUT
    { path: '/centrales', icon: Sun, label: 'Centrales Solaires', permission: PERMISSIONS.VIEW_POWER_PLANTS }, // ✅ AJOUT}
  ];

  // Filtrer les items selon les permissions de l'utilisateur
  const visibleMenuItems = menuItems.filter(item => hasPermission(item.permission));

  // Récupérer le nombre d'alertes non lues
  useEffect(() => {
    if (hasPermission(PERMISSIONS.VIEW_ALERTS)) {
      fetchUnreadAlerts();
      const interval = setInterval(fetchUnreadAlerts, 30000);
      return () => clearInterval(interval);
    }
  }, []);

  const fetchUnreadAlerts = async () => {
    try {
      const res = await alertsService.getUnreadCount();
      setUnreadAlertsCount(res.data?.unread_count || 0);
    } catch (error) {
      console.error('Erreur récupération alertes:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;

  // Obtenir le badge d'alerte pour un item
  const getItemBadge = (item) => {
    if (item.path === '/alertes' && unreadAlertsCount > 0) {
      return unreadAlertsCount;
    }
    return 0;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
      {/* Overlay pour mobile */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 h-full bg-gradient-to-b from-blue-900 to-blue-800 text-white
          transition-all duration-300 z-40
          ${isMobile ? (mobileMenuOpen ? 'translate-x-0' : '-translate-x-full') : ''}
          ${isMobile ? 'w-64' : (sidebarOpen ? 'w-64' : 'w-20')}
        `}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-blue-700">
          {showLabels && (
            <div className="flex items-center space-x-2">
              <Zap className="w-8 h-8 text-yellow-400 electric-icon" fill="currentColor" />
              <span className="text-xl font-bold">MAD POWER</span>
            </div>
          )}
          
          {/* Toggle : caché sur mobile, il est dans le header */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg hover:bg-blue-700 transition-colors hidden lg:block"
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>

          {/* ✅ NOUVEAU : bouton fermer visible sur mobile dans la sidebar elle-même */}
          {isMobile && (
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="p-2 rounded-lg hover:bg-blue-700 transition-colors lg:hidden"
            >
              <X size={20} />
            </button>
          )}
        </div>

        {/* Menu Items - Filtrés par permissions */}
        <nav className="mt-6 px-3 space-y-1 h-[calc(100vh-180px)] overflow-y-auto">
          {visibleMenuItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            const badge = getItemBadge(item);

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`
                  flex items-center px-4 py-3 rounded-lg transition-all relative
                  ${active
                    ? 'bg-blue-700 text-white shadow-lg'
                    : 'text-blue-100 hover:bg-blue-700/50'
                  }
                `}
              >
                <div className="relative">
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {/* Badge visible même en mode icône seule (desktop réduit) */}
                  {badge > 0 && !showLabels && (
                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center animate-pulse ring-2 ring-blue-900">
                      {badge > 9 ? '9+' : badge}
                    </span>
                  )}
                </div>
                {/* ✅ Icône + nom affichés ensemble (desktop déplié OU mobile ouvert) */}
                {showLabels && (
                  <span className="ml-3 flex items-center gap-2">
                    {item.label}
                    {badge > 0 && (
                      <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full animate-pulse">
                        {badge}
                      </span>
                    )}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Info utilisateur en bas */}
        {showLabels && (
          <div className="absolute bottom-4 left-4 right-4 p-3 bg-blue-950/50 rounded-lg text-xs">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="w-4 h-4 text-blue-300" />
              <span className="font-semibold text-blue-200">Rôle actuel</span>
            </div>
            <p className="text-blue-300 capitalize font-medium">{user?.role}</p>
          </div>
        )}
      </aside>

      {/* Main Content */}
      <div
        className={`
          transition-all duration-300
          ${isMobile ? 'ml-0' : (sidebarOpen ? 'ml-64' : 'ml-20')}
        `}
      >
        {/* Top Bar */}
        <header className="h-16 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between px-4 lg:px-6 sticky top-0 z-30 shadow-sm">
          <div className="flex items-center space-x-4">
            {/* Bouton menu hamburger mobile */}
            {isMobile && (
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors lg:hidden"
              >
                <Menu size={24} />
              </button>
            )}

            <h1 className="text-lg lg:text-xl font-semibold text-gray-800 dark:text-white">
              {visibleMenuItems.find(item => item.path === location.pathname)?.label || 'MAD POWER'}
            </h1>

            {/* Badge d'alertes dans le header */}
            {hasPermission(PERMISSIONS.VIEW_ALERTS) && unreadAlertsCount > 0 && location.pathname !== '/alertes' && (
              <Link
                to="/alertes"
                className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors text-sm font-medium"
              >
                <Bell className="w-4 h-4 animate-pulse" />
                {unreadAlertsCount} nouvelle(s) alerte(s)
              </Link>
            )}
          </div>

          {/* User Menu */}
          <div className="flex items-center space-x-2 lg:space-x-4">
            {/* Notification Bell */}
            {hasPermission(PERMISSIONS.VIEW_ALERTS) && (
              <Link
                to="/alertes"
                className="relative p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title="Alertes"
              >
                <Bell className="w-5 h-5" />
                {unreadAlertsCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
                    {unreadAlertsCount > 9 ? '9+' : unreadAlertsCount}
                  </span>
                )}
              </Link>
            )}

            {/* User Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-2 lg:space-x-3 px-2 lg:px-4 py-2 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              >
                <div className="w-8 lg:w-10 h-8 lg:h-10 bg-blue-600 rounded-full flex items-center justify-center">
                  <User className="w-5 lg:w-6 h-5 lg:h-6 text-white" />
                </div>
                <div className="text-sm text-left hidden md:block">
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {user?.full_name || user?.username}
                  </p>
                  <p className="text-gray-500 dark:text-gray-400 text-xs capitalize">
                    {user?.role}
                  </p>
                </div>
              </button>

              {/* Dropdown Menu */}
              {showUserMenu && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowUserMenu(false)}
                  />
                  <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                    <Link
                      to="/profile"
                      onClick={() => setShowUserMenu(false)}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
                    >
                      <User className="w-5 h-5" />
                      Mon Profil
                    </Link>
                    {isAdmin && (
                      <Link
                        to="/settings"
                        onClick={() => setShowUserMenu(false)}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
                      >
                        <Settings className="w-5 h-5" />
                        Paramètres
                      </Link>
                    )}
                    <hr className="border-gray-200 dark:border-gray-700" />
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        handleLogout();
                      }}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors text-red-600 dark:text-red-400 w-full text-left rounded-b-lg"
                    >
                      <LogOut className="w-5 h-5" />
                      Déconnexion
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;